import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { getSourceBrandLocationsEndpointIdentifier } from './use-get-source-brand-locations-api';

const endpoint = 'ums/v1/crm/management/source-brand-locations';
export const updateSourceBrandLocationEndpointIdentifier = endpoint;

interface BodyType {
  geoLocationId?: number;
  sourceUrl?: string;
  name?: string;
  address?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  rating?: number;
  ratingsTotal?: number;
  reviewsTotal?: number;
  priceLevel?: number;
  avgCostPerCouple?: number;
  avgCostCurrency?: string;
  businessStatus?: string;
  phoneNumber?: string;
  website?: string;
}

interface MutationPayload {
  id: number;
  data: BodyType;
}

type TData = {
  message: string;
};
type TError = AxiosError<{ message: string }>;
type Options = UseMutationOptions<TData, TError, MutationPayload>;
type ReturnType = UseMutationResult<TData, TError, MutationPayload>;

export function useUpdateSourceBrandLocationApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async ({ id, data }: MutationPayload) => {
    try {
      const response = await http.put(`${endpoint}/${id}`, data);

      toast({
        description: 'Source brand location updated successfully',
        status: 'success',
      });

      return response.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  const { ...otherOptions } = options || {};

  return useMutationWrapper(mutationFn, {
    onSuccess: (_, variables) => {
      // Invalidate main list queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryEndpoint, params] = query.queryKey;
          return (
            queryEndpoint === getSourceBrandLocationsEndpointIdentifier &&
            typeof params === 'object' &&
            params !== null &&
            !Array.isArray(params)
          );
        },
      });

      // Invalidate specific location details
      queryClient.invalidateQueries([getSourceBrandLocationsEndpointIdentifier, 'details', variables.id]);
    },
    ...otherOptions,
  });
}
